'use client';

// CelebrationModal - Celebration modal shown after clarification phase

import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Zoom, { type ZoomProps } from '@mui/material/Zoom';
import type { TransitionProps } from '@mui/material/transitions';
import { forwardRef, type ReactElement } from 'react';
import { useRouter } from 'next/navigation';
import { Button, BotyIcon } from '@boty/ui';
import { useTranslations, useLocale } from 'next-intl';

// Wrapper to fix type incompatibility between Zoom and Dialog's TransitionComponent
// with exactOptionalPropertyTypes enabled
const ZoomTransition = forwardRef<
  HTMLDivElement,
  TransitionProps & { children: ReactElement }
>(function ZoomTransition(props, ref) {
  return <Zoom ref={ref} {...(props as ZoomProps)} />;
});

export interface CelebrationModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Callback when user clicks "See it in action" or dismisses modal */
  onProceedToTesting: () => void;
  /** Optional callback for share functionality (MVP: shows toast if not provided) */
  onShare?: () => void;
}

/**
 * CelebrationModal - Celebration modal component
 *
 * Displayed after the clarification phase completes to celebrate bot readiness.
 *
 * Features:
 * - Animated Boty mascot icon with bounce effect
 * - "See it in action" primary CTA
 * - "Share it" secondary CTA (MVP: shows auth required toast)
 * - Backdrop blur effect
 * - Responsive design (full-width on mobile)
 * - RTL support via CSS logical properties
 * - Any dismissal (backdrop click, escape, buttons) proceeds to testing
 */
export function CelebrationModal({
  open,
  onProceedToTesting,
  onShare,
}: CelebrationModalProps) {
  const t = useTranslations('creator.celebration');
  const tAuth = useTranslations('creator.auth');
  const router = useRouter();
  const locale = useLocale();

  const handleShare = () => {
    if (onShare) {
      onShare();
    } else {
      // Navigate to quick-signup to get shareable link (locale-aware)
      router.push(`/${locale}/quick-signup`);
    }
  };

  const handleClose = () => {
    // Any close action proceeds to testing
    onProceedToTesting();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      TransitionComponent={ZoomTransition}
      TransitionProps={{ timeout: 300 }}
      slotProps={{
        backdrop: {
          sx: { backdropFilter: 'blur(4px)' },
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: 3,
          mx: { xs: 2, md: 'auto' },
          maxWidth: { xs: 'calc(100% - 32px)', md: 480 },
        },
      }}
    >
      <DialogContent
        sx={{
          p: { xs: 3, md: 4 },
          textAlign: 'center',
        }}
      >
        {/* Boty mascot with bounce animation (3 cycles) */}
        <Box sx={{ mb: 2 }}>
          <Box
            sx={{
              display: 'inline-block',
              animation: 'bounce 0.6s ease-in-out 3',
              '@keyframes bounce': {
                '0%, 100%': { transform: 'translateY(0)' },
                '50%': { transform: 'translateY(-10px)' },
              },
            }}
          >
            <BotyIcon size={100} variant="full" />
          </Box>
        </Box>

        {/* Headline */}
        <Typography
          variant="h4"
          fontWeight="bold"
          gutterBottom
          sx={{ fontSize: { xs: '1.5rem', md: '2rem' } }}
        >
          {t('title')}
        </Typography>

        {/* Encouraging copy */}
        <Typography
          color="text.secondary"
          sx={{ mb: 4, px: { xs: 0, md: 2 } }}
        >
          {t('description')}
        </Typography>

        {/* Action buttons */}
        <Stack spacing={2}>
          <Button
            variant="contained"
            size="large"
            onClick={onProceedToTesting}
            fullWidth
            sx={{ minHeight: 48 }}
          >
            {t('seeItInAction')}
          </Button>
          <Button
            variant="outlined"
            size="large"
            onClick={handleShare}
            fullWidth
            sx={{ minHeight: 48 }}
          >
            {tAuth('getShareableLink')}
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

export default CelebrationModal;
